import React, { useCallback, useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { toast } from 'sonner';
import {
  ArrowLeft,
  BookOpen,
  Loader2,
  Plus,
  Pencil,
  Trash2,
  Save,
  X,
  Search,
} from 'lucide-react';
import { useAdminAuth } from './AdminAuthContext';
import { adminApi } from './adminApi';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

/**
 * AdminKBEditorPage — runtime editor for the SupportArticles
 * knowledgebase that powers the AI Omnibar's documentation drawer.
 *
 * Two panes:
 *   • Left  — searchable list of every article with quick delete.
 *   • Right — markdown editor + live preview for create / update.
 *
 * All operations go through /api/v1/admin/support-articles which is
 * gated by `get_current_superadmin` so only Emergent staff can write.
 */
export function AdminKBEditorPage() {
  const { admin, loading } = useAdminAuth();
  const [articles, setArticles] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // article | { id: null } for new
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(() => {
    if (!admin) return;
    setLoadingList(true);
    adminApi
      .get('/v1/admin/support-articles')
      .then((r) => setArticles(r.data || []))
      .catch(() => toast.error('Could not load articles'))
      .finally(() => setLoadingList(false));
  }, [admin]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (loading) return null;
  if (!admin) return <Navigate to="/admin/login" replace />;

  const filtered = articles.filter((a) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      a.title.toLowerCase().includes(q) ||
      a.category.toLowerCase().includes(q) ||
      (a.keywords_array || []).some((k) => k.toLowerCase().includes(q))
    );
  });

  const startNew = () => setEditing({
    id: null,
    title: '',
    category: 'getting-started',
    content_markdown: '**New article body** — supports markdown.',
    keywords_array: [],
  });

  const saveArticle = async () => {
    if (!editing.title.trim() || !editing.content_markdown.trim()) {
      toast.error('Title and content are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: editing.title.trim(),
        category: editing.category.trim() || 'general',
        content_markdown: editing.content_markdown,
        keywords_array: (editing.keywords_array || []).map((k) => k.trim()).filter(Boolean),
      };
      if (editing.id) {
        await adminApi.put(`/v1/admin/support-articles/${editing.id}`, payload);
        toast.success('Article updated');
      } else {
        await adminApi.post('/v1/admin/support-articles', payload);
        toast.success('Article created');
      }
      setEditing(null);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const deleteArticle = async (a) => {
    if (!window.confirm(`Delete "${a.title}"?`)) return;
    try {
      await adminApi.delete(`/v1/admin/support-articles/${a.id}`);
      toast.success('Deleted');
      if (editing?.id === a.id) setEditing(null);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div
      data-testid="admin-kb-editor-page"
      className="min-h-screen"
      style={{ background: 'radial-gradient(circle at 0% 0%, #1e293b 0%, #0f172a 50%, #020617 100%)' }}
    >
      <header className="border-b border-slate-800 bg-slate-950/60 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin" data-testid="admin-kb-back" className="w-9 h-9 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center hover:bg-slate-800">
              <ArrowLeft className="w-4 h-4 text-slate-300" />
            </Link>
            <div className="w-9 h-9 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
              <BookOpen className="w-4 h-4 text-amber-400" />
            </div>
            <div>
              <div className="text-white font-semibold">Knowledge Base Editor</div>
              <div className="text-xs text-slate-400">Articles powering the AI Omnibar documentation drawer</div>
            </div>
          </div>
          <Button
            onClick={startNew}
            data-testid="admin-kb-new-btn"
            className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
          >
            <Plus className="w-4 h-4 mr-1" /> New Article
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 grid lg:grid-cols-[1fr_1.4fr] gap-6">
        {/* ---- LEFT: list pane ---- */}
        <section className="bg-slate-900/70 border border-slate-800 rounded-2xl p-4 space-y-3">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              data-testid="admin-kb-search"
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search title, category, keyword…"
              className="h-9 pl-8 bg-slate-950 border-slate-700 text-slate-100 placeholder:text-slate-500"
            />
          </div>
          {loadingList && (
            <div className="text-slate-400 flex items-center gap-2 py-4">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          )}
          {!loadingList && filtered.length === 0 && (
            <div className="text-center text-slate-500 py-10 border border-dashed border-slate-800 rounded-xl">
              No articles yet — click "New Article" to start.
            </div>
          )}
          <div className="space-y-2">
            {filtered.map((a) => (
              <div
                key={a.id}
                data-testid={`admin-kb-row-${a.id}`}
                className={`group rounded-xl border ${editing?.id === a.id ? 'border-amber-500/60 bg-amber-500/5' : 'border-slate-800 bg-slate-950/40 hover:bg-slate-900/60'} p-3 transition-colors`}
              >
                <div className="flex items-start justify-between gap-2">
                  <button
                    type="button"
                    onClick={() => setEditing({ ...a, keywords_array: a.keywords_array || [] })}
                    className="flex-1 min-w-0 text-left"
                    data-testid={`admin-kb-edit-${a.id}`}
                  >
                    <div className="text-sm font-semibold text-slate-100 truncate">{a.title}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5 uppercase tracking-wider">{a.category}</div>
                    {(a.keywords_array || []).length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {a.keywords_array.slice(0, 4).map((k) => (
                          <span key={k} className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-800 text-slate-300 border border-slate-700">{k}</span>
                        ))}
                        {a.keywords_array.length > 4 && <span className="text-[10px] text-slate-500">+{a.keywords_array.length - 4}</span>}
                      </div>
                    )}
                  </button>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => setEditing({ ...a, keywords_array: a.keywords_array || [] })}
                      className="p-1.5 rounded text-slate-400 hover:text-amber-400 hover:bg-slate-800"
                      title="Edit"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteArticle(a)}
                      className="p-1.5 rounded text-slate-400 hover:text-red-400 hover:bg-red-900/30"
                      title="Delete"
                      data-testid={`admin-kb-delete-${a.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ---- RIGHT: editor pane ---- */}
        <section className="bg-slate-900/70 border border-slate-800 rounded-2xl p-5">
          {!editing ? (
            <div className="h-full min-h-[400px] flex flex-col items-center justify-center text-center text-slate-500">
              <BookOpen className="w-10 h-10 text-slate-700 mb-3" />
              <p className="text-sm">Pick an article on the left to edit, or click <span className="text-amber-400 font-semibold">New Article</span> to add one.</p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="admin-kb-editor-pane">
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-white font-semibold">
                  {editing.id ? 'Edit Article' : 'New Article'}
                </h2>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setEditing(null)}
                    className="border-slate-700 bg-slate-950 text-slate-200 hover:bg-slate-800 hover:text-white"
                    data-testid="admin-kb-cancel-btn"
                  >
                    <X className="w-4 h-4 mr-1" /> Cancel
                  </Button>
                  <Button
                    onClick={saveArticle}
                    disabled={saving}
                    className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
                    data-testid="admin-kb-save-btn"
                  >
                    {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                    {editing.id ? 'Save changes' : 'Create article'}
                  </Button>
                </div>
              </div>

              <div className="grid sm:grid-cols-[1fr_180px] gap-3">
                <div>
                  <Label className="text-slate-300 text-xs">Title</Label>
                  <Input
                    value={editing.title}
                    onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                    className="h-10 bg-slate-950 border-slate-700 text-slate-100"
                    data-testid="admin-kb-title-input"
                  />
                </div>
                <div>
                  <Label className="text-slate-300 text-xs">Category</Label>
                  <Input
                    value={editing.category}
                    onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                    className="h-10 bg-slate-950 border-slate-700 text-slate-100"
                    data-testid="admin-kb-category-input"
                    placeholder="getting-started / gst / inventory…"
                  />
                </div>
              </div>

              <div>
                <Label className="text-slate-300 text-xs">Keywords (comma-separated)</Label>
                <Input
                  value={(editing.keywords_array || []).join(', ')}
                  onChange={(e) => setEditing({
                    ...editing,
                    keywords_array: e.target.value.split(',').map((k) => k.trim()).filter(Boolean),
                  })}
                  placeholder="invoice, create, billing"
                  className="h-10 bg-slate-950 border-slate-700 text-slate-100"
                  data-testid="admin-kb-keywords-input"
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  Used by the omnibar's keyword-match scorer — keep them lowercase and concise.
                </p>
              </div>

              <div className="grid lg:grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-300 text-xs">Markdown body</Label>
                  <Textarea
                    value={editing.content_markdown}
                    onChange={(e) => setEditing({ ...editing, content_markdown: e.target.value })}
                    rows={14}
                    className="bg-slate-950 border-slate-700 text-slate-100 font-mono text-xs"
                    data-testid="admin-kb-body-input"
                  />
                </div>
                <div>
                  <Label className="text-slate-300 text-xs">Live preview</Label>
                  <div
                    data-testid="admin-kb-preview"
                    className="prose prose-sm prose-invert max-w-none p-4 bg-slate-950 border border-slate-800 rounded-md min-h-[14rem] overflow-y-auto"
                  >
                    <ReactMarkdown>{editing.content_markdown || '_Nothing to preview yet._'}</ReactMarkdown>
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

export default AdminKBEditorPage;
